// ── هويّةُ عمليّة احتساب الرواتب — قراراتٌ خالصة ───────────────────────────────
//
// **بلا أيّ `import` عمداً**: تُشغَّل من حارسٍ في حزمةٍ أخرى بلا React ولا
// متصفّحٍ ولا تخزين. قرارٌ يسكن ملفَّ شاشةٍ لا يستطيع حارسٌ أن **يشغّله** —
// يقرؤه نصّاً، والنصُّ يُخدَع.
//
// القاعدة: المفتاحُ هويّةُ **محاولة** لا هويّةُ شهر.
//   • يبقى ما دامت المحاولةُ غيرَ محسومة ⇒ إعادةُ الإرسال مصالحةٌ لا كتابة؛
//   • ويُرفع عند نتيجةٍ نهائيّةٍ مؤكَّدة ⇒ الضغطةُ التالية احتسابٌ جديدٌ صريح.

/** الحالاتُ التي تُنهي العمليّة — وما عداها يُستأنف بالهويّة نفسِها. */
const SETTLED = ["complete", "partial", "failed", "abandoned"] as const;

/**
 * هل حُسمت هذه المحاولةُ نهائيّاً؟
 *
 * ⚠ `running` و`uncertain` **ليستا نهائيّتين**: الأولى لم تُكتب خاتمتُها فلا
 * يُعرف ما استقرّ، والثانية ضاع إقرارُها. استبدالُ أيٍّ منهما بمحاولةٍ جديدة
 * يكتب فوق نتيجةٍ مجهولة — وهو بعينه ما يصنع الصفَّ مرّتين.
 */
export function isSettledRunState(state: unknown): boolean {
  return typeof state === "string" && (SETTLED as readonly string[]).includes(state);
}

/**
 * موضعُ الهويّة الدائم — **أو `null` فلا تُخزَّن**.
 *
 * متصفّحٌ يتناوب عليه مستخدمان من شركتين ورمزٌ لا يُفكّ ⇒ نطاقٌ مشترَك ⇒
 * مفتاحُ عمليّةِ إحداهما يُقرأ في الأخرى. فحين تتعذّر معرفةُ الشركة تبقى
 * الهويّةُ في الذاكرة وحدَها: تُفقد بإعادة التحميل ولا تُسرَّب أبداً.
 */
export function opKeyStorageKey(tenantScope: string | null, month: string): string | null {
  if (!tenantScope || !month) return null;
  return `jisr.payroll.opkey.${tenantScope}.${month}`;
}

/** هويّةٌ جديدة. `rand`/`now` وسيطان كي تُختبر بلا اعتمادٍ على ساعةٍ أو عشوائيّة. */
export function mintOpKey(month: string, now: number, rand: string): string {
  return `web-${month}-${now.toString(36)}-${rand}`;
}

/** الشركةُ من ادّعاء `tenantId` في الرمز — و`null` إن تعذّر، فلا تُخمَّن. */
export function tenantScopeFromToken(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const part = raw.split(".")[1];
    if (!part) return null;
    const json = typeof atob === "function"
      ? atob(part.replace(/-/g, "+").replace(/_/g, "/"))
      : Buffer.from(part, "base64").toString("utf8");
    const claim = JSON.parse(json) as { tenantId?: unknown };
    return typeof claim?.tenantId === "string" && claim.tenantId ? claim.tenantId : null;
  } catch { return null; }
}

// ── دورةُ حياة المفتاح: سياقٌ ثابتٌ يُلتقط عند الإرسال ويُحسم به ─────────────
//
// (مراجعة 2026-10-21) كانت الذاكرةُ بالشهر وحدَه والحسمُ يقرأ الشركةَ **لحظةَ الردّ**:
//   • بعد تبدّل الحساب كانت ذاكرةُ الشهر تُعيد مفتاحَ الشركة الأولى في الثانية ولو كان
//     للثانية مفتاحٌ معلَّقٌ محفوظ؛
//   • وردُّ احتسابِ الأولى المتأخّر يمحو مفتاحَ الثانية المعلَّق.
// فالمفتاحُ يُحلّ مرّةً بشركة الرمز المُرسَل وشهره، والسياقُ نفسُه هو ما يُحسم به،
// والمحوُ مشروطٌ بأن يكون المخزَّنُ هو **مفتاحَ هذا الإرسال** لا غيرَه.

/** تخزينٌ يُمرَّر (localStorage أو بديلٌ في الاختبار) — كلُّ نداءٍ قد يرمي. */
export interface OpKeyStore { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

/** سياقُ إرسالٍ واحد: لا يتغيّر بعد الالتقاط. */
export interface OpKeyCtx { readonly tenant: string | null; readonly month: string; readonly key: string }

/** موضعُ الذاكرة: الشركةُ + الشهر. شركةٌ مجهولة ⇒ `null` فلا ذاكرةَ مشترَكةَ بين مجهولَين. */
export function opKeyMemSlot(tenant: string | null, month: string): string | null {
  return tenant && month ? `${tenant}#${month}` : null;
}

/**
 * مفتاحُ هذا الإرسال: الذاكرةُ ثمّ المخزَّنُ **لموضع هذه الشركة وهذا الشهر وحدَهما**، وإلّا
 * يُسكّ جديد. شركةٌ مجهولة ⇒ مفتاحٌ جديدٌ لا يُحفظ في أيّ مكان (لا يُسرَّب ولا يُستأنَف).
 */
export function resolveOpKey(mem: Record<string, string>, store: OpKeyStore | null, tenant: string | null,
  month: string, mint: () => string): OpKeyCtx {
  const slot = opKeyMemSlot(tenant, month);
  const sk = opKeyStorageKey(tenant, month);
  if (!slot || !sk) return Object.freeze({ tenant, month, key: mint() });
  let key = mem[slot] ?? null;
  if (!key && store) { try { key = store.getItem(sk); } catch { key = null; } }
  if (!key) {
    key = mint();
    if (store) { try { store.setItem(sk, key); } catch { /* تخزينٌ محجوب: الذاكرةُ وحدَها */ } }
  }
  mem[slot] = key;
  return Object.freeze({ tenant, month, key });
}

/**
 * حسمٌ بالسياق الملتقَط لا بالحالة الراهنة: يُرفع المفتاحُ من ذاكرة موضعه ومن مخزَّنه **فقط إن
 * كان هو نفسَه** — مفتاحٌ آخر في الموضع (محاولةٌ لاحقة، أو شركةٌ أخرى) لا يُمسّ.
 */
export function settleOpKeyCtx(mem: Record<string, string>, store: OpKeyStore | null, ctx: OpKeyCtx, runState: unknown): void {
  if (!isSettledRunState(runState)) return;
  const slot = opKeyMemSlot(ctx.tenant, ctx.month);
  if (slot && mem[slot] === ctx.key) delete mem[slot];
  const sk = opKeyStorageKey(ctx.tenant, ctx.month);
  if (!sk || !store) return;
  try { if (store.getItem(sk) === ctx.key) store.removeItem(sk); } catch { /* */ }
}
